/**
 * Shared visit-header fields for dedicated nurse notes:
 * billing code + 12-hour Time In / Time Out (AM/PM).
 */

export const PT_BILLING_CODE_OPTIONS = [
  { value: "G0151", label: "G0151 — Home Health Physical Therapy" },
  { value: "G0157", label: "G0157 — PT Assistant (PTA)" },
  { value: "97110", label: "97110 — Therapeutic Exercise" },
  { value: "97112", label: "97112 — Neuromuscular Re-education" },
  { value: "97116", label: "97116 — Gait Training" },
  { value: "97530", label: "97530 — Therapeutic Activities" },
  { value: "G0152", label: "G0152 — Home Health Occupational Therapy" },
  { value: "G0153", label: "G0153 — Home Health Speech Therapy" },
];

/** 15-minute slots across a full day — matches skilled-visit note time pickers. */
export const TIME_OPTIONS_15 = (() => {
  const options = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const hour24 = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const period = hour24 < 12 ? "AM" : "PM";
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const label = `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
    options.push(label);
  }
  return options;
})();

export const emptyPtVisitMeta = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",
});

const TIME_RE = /^(\d{1,2}):([0-5]\d)\s*(AM|PM)$/i;

export const parseTime12 = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const match = raw.match(TIME_RE);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 1 || hour > 12) return null;
  return {
    hour: String(hour),
    minute: String(minute).padStart(2, "0"),
    period: match[3].toUpperCase(),
  };
};

export const formatTime12 = (hour, minute, period) => {
  if (!hour || !period) return "";
  const h = Number(hour);
  const m = Number(minute || 0);
  if (!h || h < 1 || h > 12 || Number.isNaN(m) || m < 0 || m > 59) return "";
  return `${h}:${String(m).padStart(2, "0")} ${period}`;
};

/** Normalize any valid 12h string onto a display label. */
export const normalizeTimeOption = (value) => {
  const parsed = parseTime12(value);
  if (!parsed) return "";
  return formatTime12(parsed.hour, parsed.minute, parsed.period);
};

/** Minutes from midnight for ordering Time In vs Time Out. */
export const time12ToMinutes = (value) => {
  const parsed = parseTime12(value);
  if (!parsed) return null;
  let hour = Number(parsed.hour) % 12;
  if (parsed.period === "PM") hour += 12;
  return hour * 60 + Number(parsed.minute);
};

export const isValidTime12 = (value) => Boolean(parseTime12(value));

/**
 * Soft validation for Time In / Time Out.
 * Empty pair is allowed on draft save; send-to-office can require them.
 */
export const validateVisitTimes = (timeIn, timeOut, { required = false } = {}) => {
  const inVal = String(timeIn || "").trim();
  const outVal = String(timeOut || "").trim();

  if (!inVal && !outVal) {
    return required ? "Enter Time In and Time Out." : null;
  }
  if (required && (!inVal || !outVal)) {
    return "Enter both Time In and Time Out.";
  }
  if (inVal && !outVal) return null;
  if (!inVal && outVal) return null;
  if (!isValidTime12(inVal)) {
    return "Time In must be a valid time with AM or PM (e.g. 8:45 AM).";
  }
  if (!isValidTime12(outVal)) {
    return "Time Out must be a valid time with AM or PM (e.g. 9:30 AM).";
  }
  const start = time12ToMinutes(inVal);
  const end = time12ToMinutes(outVal);
  if (start == null || end == null) return "Enter valid Time In and Time Out.";
  if (end <= start) {
    return "Time Out must be after Time In.";
  }
  return null;
};

export const validateNurseVisitMeta = (
  { billingCode, timeIn, timeOut } = {},
  { requireBilling = false, requireTimes = false } = {},
) => {
  if (requireBilling && !String(billingCode || "").trim()) {
    return "Select a billing code.";
  }
  return validateVisitTimes(timeIn, timeOut, { required: requireTimes });
};

/** Prefer payload values, then dedicated columns from the API. */
export const pickPtVisitMeta = (loadedPayload = {}, data = {}) => ({
  billingCode: String(loadedPayload.billingCode || data.billingCode || "").trim(),
  timeIn: normalizeTimeOption(loadedPayload.timeIn || data.timeIn || ""),
  timeOut: normalizeTimeOption(loadedPayload.timeOut || data.timeOut || ""),
});

export const assertPtVisitMetaForFlag = (
  values,
  flag,
  { requireTimes = true } = {},
) =>
  validateNurseVisitMeta(values, {
    requireBilling: flag === "send_to_office",
    requireTimes: requireTimes && flag === "send_to_office",
  });

export const buildPtVisitMetaProps = ({
  values,
  setValue,
  setValues,
  canEdit,
  printMode,
}) => ({
  billingCode: values.billingCode || "",
  timeIn: values.timeIn || "",
  timeOut: values.timeOut || "",
  billingOptions: PT_BILLING_CODE_OPTIONS,
  canEdit,
  printMode,
  onBillingCodeChange: (next) => setValue("billingCode", next),
  onTimeInChange: (next) => {
    if (typeof setValues === "function") {
      setValues((prev) => {
        const out = prev.timeOut || "";
        const start = next ? time12ToMinutes(next) : null;
        const end = out ? time12ToMinutes(out) : null;
        const clearOut = start != null && end != null && end <= start;
        return {
          ...prev,
          timeIn: next,
          timeOut: clearOut ? "" : prev.timeOut,
        };
      });
      return;
    }
    setValue("timeIn", next);
  },
  onTimeOutChange: (next) => setValue("timeOut", next),
  error: validateVisitTimes(values.timeIn, values.timeOut) || "",
});
